<!--
  Provides the template for the application title and summary details at the top of
  the application.
-->
<template>
  <div class="ApplicationSummary">
    <div v-if="this.applicationStatus == 'NEW'">
      <AdsButton class="mr-2 withdrawBtn" x-large secondary @click="openStatusDialog('Assigned')"
        buttonText="Assign to me" icon="mdi-account" aria-label="Assign" />
    </div>
    <div v-if="this.isProcessingCOStatus">
      <div class="btnPanel">
        <AdsButton class="mr-2 withdrawBtn" x-large secondary @click="openStatusDialog('DISMISSED')" buttonText="Dismiss"
          icon="mdi-cancel" aria-label="Dismiss" />
        <AdsButton class="mr-2 withdrawBtn" x-large secondary @click="openStatusDialog('DENIED')" buttonText="Deny"
          icon="remove_circle_outline" aria-label="Deny" />
        <AdsButton class="mr-2 withdrawBtn" x-large secondary @click="openStatusDialog('UNQUALIFIED')"
          buttonText="Disqualify" icon="mdi-close-circle-outline" aria-label="Disqualify" />
      </div>
      <div class="btnPanel">
        <AdsButton v-if="this.applicationStatus != 'PROCESSING_APP'" x-large aria-label="Processing"
          buttonText="Processing" @click="openStatusDialog('PROCESSING_APP')" icon="mdi-comment-processing-outline"
          class="sendToApproveButton" />
        <AdsButton x-large aria-label="Request to Approve" buttonText="Request to Approve"
          @click="openStatusDialog('APPROVAL_REQ')" icon="mdi-send-outline" class="sendToApproveButton" />
        <AdsButton x-large aria-label="Request Information" buttonText="Request Information"
          v-if="this.applicationStatus != 'INFO_REQ'" @click="openStatusDialog('INFO_REQ')" icon="mdi-information-outline"
          class="sendToApproveButton" />
      </div>
    </div>
    <div v-if="this.isApproveStatusAOStatus">
      <div class="btnPanel">
        <AdsButton x-large aria-label="Approve" buttonText="Approve" @click="openStatusDialog('APPROVAL_REQ_APPROVED')"
          icon="mdi-checkbox-marked-circle" class="sendToApproveButton" />
        <AdsButton x-large aria-label="Request Denied" buttonText="Request Denied"
          @click="openStatusDialog('APPROVAL_REQ_DENIED')" icon="mdi-send-outline" class="sendToApproveButton" />
      </div>
    </div>
    <div v-if="this.isRequestApprovedCOStatus">
      <AdsButton x-large aria-label="Complete" buttonText="Complete" @click="openStatusDialog('COMPLETED')"
          icon="mdi-checkbox-multiple-marked-outline" class="sendToApproveButton" />
    </div>
    <ChangeStatusModal v-if="showChangeStatus" v-model="showChangeStatus" 
    persistent :current-status="applicationStatus" :status="newStatus" :clientName="clientName"
      @confirm="confirmStatusChange" />
  </div>
</template>

<script>
import AdsButton from '@/components/app/AdsButton.vue'
import fieldHelperMixin from '@/helpers/fieldHelperMixin'
import ChangeStatusModal from '@/components/application/ea/ChangeStatusModal'
import { EA_STATUS, EA_STATUS_CHANGED_ICONS } from '@/constants'
const STATUS_PROPS = {
  // Offered statuses have the same props
  [EA_STATUS.ASSIGNED]: {
    type: 'info',
    icon: 'lock_outline',
    text: 'The parent/carer has chosen not to accept this offer.',
    subtext: 'No further action can be taken at this time.',
    cssClass: 'greyBorderAndIcon'
  },
  [EA_STATUS.DENIED]: {
    type: 'error',
    icon: 'lock_outline',
    text: 'This application has been declined.'
  },
  [EA_STATUS.APPROVAL_REQ_DENIED]: {
    type: 'error',
    icon: 'lock_outline',
    text: 'This application approval has been Denied.'
  },
  [EA_STATUS.COMPLETED]: {
    type: 'success',
    icon: 'send',
    text:
      'This offer has been accepted and a full application has been submitted.'
  },
  [EA_STATUS.OFFER_EXPIRED]: {
    type: 'info',
    icon: 'lock_outline',
    text: 'This application offer has expired.',
    subtext: 'No further action can be taken at this time.',
    cssClass: 'greyBorderAndIcon'
  },
  [EA_STATUS.INFO_REQ]: {
    type: 'info',
    icon: 'lock_outline',
    text: 'This application has been withdrawn.',
    subtext: 'No further action can be taken at this time.',
    cssClass: 'greyBorderAndIcon'
  }
}

export default {
  name: 'ApplicationSummary',
  mixins: [fieldHelperMixin],
  components: { AdsButton, ChangeStatusModal, EA_STATUS },
  data() {
    return {
      openPanels: [],
      showChangeStatus: false,
      newStatus: ''
    }
  },
  computed: {
    state() {
      return this.$store.state
    },
    isCaseOfficer() {
      return this.$store.state.userGroup.includes('CaseOfficer')
    },
    isApprovalOfficer() {
      return this.$store.state.userGroup.includes('Approver')
    },
    isApproveStatusAOStatus() {

      const approvalStatuses = [
        EA_STATUS.APPROVAL_REQ,
      ]
      return this.isApprovalOfficer &&
        approvalStatuses.includes(EA_STATUS[this.applicationStatus])
    },
    isProcessingCOStatus() {
      const assignedStatuses = [
        EA_STATUS.ASSIGNED,
        EA_STATUS.PROCESSING_APP,
        EA_STATUS.INFO_REQ,
        EA_STATUS.APPROVAL_REQ_DENIED,
      ]
      return this.isCaseOfficer
        && assignedStatuses.includes(EA_STATUS[this.applicationStatus])
    },
    isRequestApprovedCOStatus() {
      const assignedStatuses = [
        EA_STATUS.APPROVAL_REQ_APPROVED,
      ]
      return this.isCaseOfficer
        && assignedStatuses.includes(EA_STATUS[this.applicationStatus])
    },
    isCompletedStatus() {
      const completedStatuses = [
        EA_STATUS.DENIED,
        EA_STATUS.UNQUALIFIED,
        EA_STATUS.COMPLETED,
        EA_STATUS.DISMISSED
      ]
      return  completedStatuses.includes(EA_STATUS[this.applicationStatus])
    },
    applicationId() {
      return this.getValue('applicationId')
    },
    applicationStatus() {
      return this.$store.state.application.applicationStatus
    },
    clientName(){
      var a = this.$store.state.application
      return  a.application.client.firstName + " " +a.application.client.lastName
    },
    statusProps() {
      return STATUS_PROPS[this.applicationStatus]
    }
  },
  methods: {
    openStatusDialog(status) {
      console.log('Testing:' + status)
      this.showChangeStatus = true
      this.newStatus = status
      this.$store.dispatch('set', ['applicationWithdrawn', 'pending'])
    },
    confirmStatusChange({
      status,
      comment,
      sendEmail,
      approverEmail,
      documents
    }) {
      // extract only timestamp/filename.ext from the full object key of schoolCode/applicationId/timestamp/filename.ext
      const newDocuments = documents
        ? documents.map(d =>
          d.key
            .split('/')
            .slice(-2)
            .join('/')
        )
        : []

      this.$store
        .dispatch('saveApplicationStatus', {
          applicationStatus: status,
          comment,
          sendEmail,
          approverEmail,
          newDocuments
        })
        .then(() => {
          this.$router.push({ path: '/' })
          let displayStatus = status

          let snackbarHtml = `the Application is now set to <b>"${displayStatus}"</b>.`
          if (sendEmail ) {
            snackbarHtml += ` An email has been sent.`
          }

          this.$store.dispatch('showSnackbar', {
            display: true,
            html: snackbarHtml,
            icon:
              EA_STATUS_CHANGED_ICONS[status] || 'mdi-checkbox-marked-circle'
          })
        })
    }
  }
}
</script>

<style scoped lang="scss">
.ApplicationSummary {
  padding: 1.5rem 3rem 1.5rem 1rem;
  display: flex;
  justify-content: flex-end;
  flex-flow: row wrap;

  .summary {
    flex: 1;
    min-width: 350px;

    h1 {
      margin-top: 0;
      font-size: 1.5rem;
      font-weight: 400;

      span {
        margin-left: 20px;
      }
    }

    .fields {
      td {
        padding-bottom: 1rem;
        vertical-align: top;
      }

      .label {
        color: $color-text-light;
        white-space: nowrap;

        &:after {
          content: ':';
        }

        padding-right: 0.5rem;
      }

      .value {
        color: black;
        padding-right: 3rem;
      }
    }
  }
}

.withdrawBtn,
.sendToApproveButton {
  margin: 0;
}

.btnPanel {
  float: Right;
}

::v-deep .v-btn:not(.v-btn--round).v-size--x-large {
  height: 44px;
}

::v-deep .theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
  background-color: $grey-darken1 !important;
  color: $ads-selection-control-gray !important;

  .v-icon {
    color: $ads-selection-control-gray !important;
  }
}

#app.v-application button.v-btn:not(.v-btn--round).review-btn {
  color: black;
}

#app.v-application button.v-btn.sendToApproveButton:focus:before {
  opacity: 0;
  /* Overrides the opacity of other :focus styles on this specific button */
}
</style>
